import {
	EnvironmentContextProvider,
	LayoutProvider,
	MainNavigation,
	config,
	createCollateralLibraryRoutes,
	createLegalRoutes,
	createNewsRoutes,
	createPeopleRoutes,
	createProjectRoutes,
	createResourceRoutes,
	useLayoutContext,
	ThemeProvider,
	type LayoutNavComponent,
	ResourceContextProvider,
	ResourceContextImpl,
	type MarketingSite,
} from '@ds-mgnl/marketing';
import type { LinkSourceProps, LinkSourceRecord } from '@dsx/react';
import { Provider as DsxProvider } from '@dsx/react';
import { forwardRef, useCallback } from 'react';
import {
	Outlet,
	Link as RouterLink,
	RouterProvider,
	createBrowserRouter,
} from 'react-router-dom';
import styles from './App.module.css';
import { ApturaLogo } from './components/ApturaLogo';
import { Footer } from './components/Footer';
import { ctaLinks, topNavData } from './components/MainNavigation/topNavData';
import { useScrollManagement } from './hooks/useScrollManagement';
import { useUtmFormTracking } from './hooks/useUtmFormTracking';
import { ErrorPage } from './pages/ErrorPage';
import { MagnoliaPage } from './pages/MagnoliaPage';
import { magnoliaPageLoader } from './pages/magnoliaPageLoader';
import { getMagnoliaBaseDamUrl, getMagnoliaBaseUrl } from './util';

const linkSources = {
	default: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<RouterLink ref={ref} {...props} to={props.href || ''} />
	)),
	external: forwardRef<HTMLAnchorElement, LinkSourceProps>((props, ref) => (
		<a ref={ref} {...props} />
	)),
	none: forwardRef<HTMLSpanElement, LinkSourceProps>((props, ref) => (
		<span ref={ref} {...props} />
	)),
} as LinkSourceRecord;

const SHOW_ON_SITE: MarketingSite = 'aptura';

const router = createBrowserRouter([
	{
		element: <RootLayout />,
		children: [
			...createResourceRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				resourcesHomeUrlPath: '/resources',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createLegalRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				legalHomeUrlPath: '/legal',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createCollateralLibraryRoutes({
				errorElement: <ErrorPage />,
				collateralLibUrlPath: '/collateral-library',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				mgnlBaseDamUrl: getMagnoliaBaseDamUrl() ?? '',
				mgnlCollateralHomePath: '/collateral-library',
				siteTitleSuffix: 'Direct Supply',
				showOnSite: SHOW_ON_SITE,
			}),
			...createPeopleRoutes({
				errorElement: <ErrorPage />,
				peopleUrlPath: '/people',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				mgnlBaseDamUrl: getMagnoliaBaseDamUrl() ?? '',
				mgnlPeopleHomePath: '/people',
				siteTitleSuffix: 'Direct Supply Aptura',
				showOnSite: SHOW_ON_SITE,
			}),
			...createProjectRoutes({
				errorElement: <ErrorPage />,
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				projectsHomeUrlPath: '/projects',
				pageConfig: config,
				showOnSite: SHOW_ON_SITE,
			}),
			...createNewsRoutes({
				newsHomeUrlPath: '/news',
				mgnlBaseUrl: getMagnoliaBaseUrl() ?? '',
				pageConfig: config,
				errorElement: <ErrorPage />,
				showOnSite: SHOW_ON_SITE,
			}),
			{
				path: '*',
				element: <MagnoliaPage />,
				loader: magnoliaPageLoader,
				errorElement: <ErrorPage />,
			},
		],
	},
]);

const resourceContext = new ResourceContextImpl('/resources', SHOW_ON_SITE);

export function App() {
	useUtmFormTracking();
	const navigationComponent = useCallback<LayoutNavComponent>(
		({ transparent }) => (
			<MainNavigation
				transparent={transparent}
				navData={topNavData}
				navLogo={ApturaLogo}
				ctaLinks={ctaLinks}
			/>
		),
		[],
	);

	return (
		<DsxProvider locale="en-US" linkSources={linkSources}>
			<ThemeProvider theme="aptura">
				<ResourceContextProvider value={resourceContext}>
					<EnvironmentContextProvider
						value={{
							damHost: getMagnoliaBaseDamUrl() ?? '',
							appHost: getMagnoliaBaseUrl() ?? '',
							isInMagnolia: false,
						}}
					>
						<LayoutProvider initialNavigationComponent={navigationComponent}>
							<RouterProvider router={router} />
						</LayoutProvider>
					</EnvironmentContextProvider>
				</ResourceContextProvider>
			</ThemeProvider>
		</DsxProvider>
	);
}

function RootLayout() {
	useScrollManagement();
	const { renderNavigation } = useLayoutContext();
	return (
		<div className={styles['site-container']}>
			{renderNavigation({})}
			<main>
				<Outlet />
			</main>
			<Footer />
		</div>
	);
}
